/**
 * SocialWordle host/spectator browser reducer state.
 *
 * Held as a single root object; the WS frame stream dispatches mutations
 * via `applyFrame`. All UI rendering subscribes to the resulting state
 * snapshot rather than touching frames directly — keeps the reducer
 * unit-testable without DOM.
 */

export type ColorState = 'correct' | 'present' | 'absent';

export interface PlayerRow {
  userId: string;
  displayName: string;
  /** Per-row color states only (no letters mid-round). */
  evaluations: ColorState[][];
  triesUsed: number;
  solved: boolean;
  failed: boolean;
  timedOut: boolean;
  timeMs: number | null;
  points: number;
}

export interface SwState {
  roundActive: boolean;
  /** GET_READY | ACTIVE | WIN | TIMEOUT — mirrors backend subState. */
  subState: string;
  roundIndex: number;
  wordLength: number;
  maxTries: number;
  countdownStartsAtMs: number | null;
  endsAtMs: number | null;
  /**
   * Server-authoritative wall-clock ms when the FSM will fire the next
   * round. Set on `sw_round_end` and cleared on the next `sw_round_open`.
   * `null` when the FSM is NOT auto-restarting (game over / aborted) — the
   * header timer hides in that case.
   */
  nextRoundAtMs: number | null;
  /** Final answer revealed after round-end. */
  revealedWord: string | null;
  players: Map<string, PlayerRow>;
  leaderboard: Array<{ rank: number; display_name: string; total_points: number }>;
}

export function initialState(): SwState {
  return {
    roundActive: false,
    subState: 'LOBBY',
    roundIndex: 0,
    wordLength: 5,
    maxTries: 6,
    countdownStartsAtMs: null,
    endsAtMs: null,
    nextRoundAtMs: null,
    revealedWord: null,
    players: new Map(),
    leaderboard: [],
  };
}

function ensurePlayer(state: SwState, userId: string, displayName: string): PlayerRow {
  let p = state.players.get(userId);
  if (!p) {
    p = {
      userId,
      displayName,
      evaluations: [],
      triesUsed: 0,
      solved: false,
      failed: false,
      timedOut: false,
      timeMs: null,
      points: 0,
    };
    state.players.set(userId, p);
  } else if (displayName && p.displayName !== displayName) {
    p.displayName = displayName;
  }
  return p;
}

/**
 * Apply a single WS frame to state. Returns the same `state` reference,
 * mutated in place — caller is expected to re-render after each call.
 */
export function applyFrame(state: SwState, frame: { type: string } & Record<string, unknown>): SwState {
  switch (frame.type) {
    case 'state_snapshot': {
      const s = frame.state as { state?: string; leaderboard_top10?: SwState['leaderboard'] } | undefined;
      if (s?.state) {
        // Backend emits "ROUND/ACTIVE" / "ROUND/GET_READY" / "PODIUM/WIN" etc.
        const parts = String(s.state).split('/');
        state.subState = parts[1] ?? parts[0] ?? 'LOBBY';
      }
      if (s?.leaderboard_top10) {
        state.leaderboard = s.leaderboard_top10.map((r, i) => ({
          rank: i + 1,
          display_name: r.display_name,
          total_points: r.total_points,
        }));
      }
      return state;
    }
    case 'sw_round_open':
      state.roundActive = true;
      state.subState = 'ACTIVE';
      state.roundIndex = Number(frame.round_index) || 0;
      state.wordLength = Number(frame.word_length) || 5;
      state.maxTries = Number(frame.max_tries) || 6;
      state.countdownStartsAtMs = (frame.countdown_starts_at_ms as number | null) ?? null;
      state.endsAtMs = (frame.ends_at_ms as number | null) ?? null;
      // A new round wins over any "Next round in" countdown carried from the
      // previous sw_round_end. Clear it so the header timer flips back to the
      // active-round display.
      state.nextRoundAtMs = null;
      state.revealedWord = null;
      // Reset per-player round data; identity rows remain.
      for (const p of state.players.values()) {
        p.evaluations = [];
        p.triesUsed = 0;
        p.solved = false;
        p.failed = false;
        p.timedOut = false;
        p.timeMs = null;
        p.points = 0;
      }
      return state;
    case 'sw_player_join': {
      ensurePlayer(state, String(frame.user_id), String(frame.display_name));
      return state;
    }
    case 'sw_player_guess': {
      const p = ensurePlayer(state, String(frame.user_id), String(frame.display_name));
      const evaluation = frame.evaluation as ColorState[] | undefined;
      if (evaluation) {
        p.evaluations.push(evaluation);
        p.triesUsed = Number(frame.attempt_num) || p.evaluations.length;
        if (frame.solved) p.solved = true;
      }
      return state;
    }
    case 'sw_player_solved': {
      const p = ensurePlayer(state, String(frame.user_id), String(frame.display_name));
      p.solved = true;
      p.triesUsed = Number(frame.attempts_used) || p.triesUsed;
      p.timeMs = (frame.time_ms as number | null) ?? null;
      p.points = Number(frame.points_awarded) || p.points;
      return state;
    }
    case 'sw_round_end': {
      state.roundActive = false;
      state.subState = 'WIN';
      state.revealedWord = String(frame.word ?? '');
      // Active-round countdown ends here. Pick up the next-round target if
      // the backend has scheduled one; null means the FSM is winding down
      // and the header should treat the timer slot as idle.
      state.endsAtMs = null;
      const nra = frame.next_round_at_ms;
      state.nextRoundAtMs = typeof nra === 'number' ? nra : null;
      const results = frame.results as
        | Array<{
            user_id: string;
            display_name: string;
            attempts: number;
            solved: boolean;
            failed: boolean;
            timed_out: boolean;
            time_ms: number | null;
            points: number;
          }>
        | undefined;
      if (results) {
        for (const r of results) {
          const p = ensurePlayer(state, r.user_id, r.display_name);
          p.solved = r.solved;
          p.failed = r.failed;
          p.timedOut = r.timed_out;
          p.triesUsed = r.attempts;
          p.timeMs = r.time_ms;
          p.points = r.points;
        }
      }
      return state;
    }
    case 'sw_session_leaderboard': {
      const rows = frame.rows as Array<{ rank: number; display_name: string; total_points: number }> | undefined;
      state.leaderboard = rows ?? [];
      return state;
    }
    case 'sw_game_over':
      state.roundActive = false;
      state.subState = 'PODIUM';
      // Game over — no further rounds, no timer.
      state.nextRoundAtMs = null;
      state.endsAtMs = null;
      return state;
    default:
      return state;
  }
}
